/** Thai time labels, day separators, avatars for the chat UI. */

const TZ = 'Asia/Bangkok';

function dayKey(d: Date): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
}

export const sameDay = (a: string | Date, b: string | Date) => dayKey(new Date(a)) === dayKey(new Date(b));

/** "วันนี้" / "เมื่อวาน" / "จันทร์ 21 ก.ย." / "21 ก.ย. 2568" (other years). */
export function dayLabel(v: string | Date, now = new Date()): string {
  const d = new Date(v);
  const k = dayKey(d);
  if (k === dayKey(now)) return 'วันนี้';
  if (k === dayKey(new Date(now.getTime() - 86400_000))) return 'เมื่อวาน';
  const sameYear = k.slice(0, 4) === dayKey(now).slice(0, 4);
  if (sameYear && now.getTime() - d.getTime() < 6 * 86400_000) return d.toLocaleDateString('th-TH', { timeZone: TZ, weekday: 'long', day: 'numeric', month: 'short' });
  return d.toLocaleDateString('th-TH', { timeZone: TZ, day: 'numeric', month: 'short', ...(sameYear ? {} : { year: 'numeric' }) });
}

export const clock = (v: string | Date) => new Date(v).toLocaleTimeString('th-TH', { timeZone: TZ, hour: '2-digit', minute: '2-digit' });

/** Chat list time: "เมื่อสักครู่", "5 นาที", "14:05", "เมื่อวาน", "21 ก.ย.". */
export function listTime(v: string | null, now = new Date()): string {
  if (!v) return '';
  const d = new Date(v);
  const diff = now.getTime() - d.getTime();
  if (diff < 60_000) return 'เมื่อสักครู่';
  if (diff < 3600_000) return `${Math.floor(diff / 60_000)} นาที`;
  if (sameDay(d, now)) return clock(d);
  const label = dayLabel(d, now);
  return label === 'เมื่อวาน' ? label : d.toLocaleDateString('th-TH', { timeZone: TZ, day: 'numeric', month: 'short' });
}

/** Activity feed: "เมื่อสักครู่", "3 นาทีที่แล้ว", "2 ชั่วโมงที่แล้ว", "เมื่อวาน 14:05", "21 ก.ย. 14:05". */
export function relativeTime(v: string, now = new Date()): string {
  const d = new Date(v);
  const diff = now.getTime() - d.getTime();
  if (diff < 60_000) return 'เมื่อสักครู่';
  if (diff < 3600_000) return `${Math.floor(diff / 60_000)} นาทีที่แล้ว`;
  if (diff < 6 * 3600_000 && sameDay(d, now)) return `${Math.floor(diff / 3600_000)} ชั่วโมงที่แล้ว`;
  const label = dayLabel(d, now);
  return `${label} ${clock(d)}`;
}

export function initials(name: string): string {
  const t = name.trim();
  if (!t) return '?';
  const parts = t.split(/[\s._@-]+/).filter(Boolean);
  const first = [...(parts[0] ?? t)][0] ?? '?';
  const second = parts.length > 1 ? [...parts[1]!][0] ?? '' : '';
  return (first + second).toUpperCase();
}

/** A stable avatar tone per person (index into the token palette in chat.css). */
export function avatarTone(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return h % 8;
}

export function duration(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

export function muteLabel(until: string | null): string | null {
  if (!until) return null;
  const d = new Date(until);
  if (d.getUTCFullYear() >= 9999) return 'ปิดเสียงจนกว่าจะเปิด';
  return `ปิดเสียงถึง ${clock(d)}${sameDay(d, new Date()) ? '' : ` ${dayLabel(d)}`}`;
}
